import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Archive, ArchiveRestore, ChevronLeft, Code2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/Layout';
import { AgentMap } from '@/components/AgentMap';
import { PortabilityActions } from '@/components/Portability';
import { TraceList } from '@/components/TraceList';
import { UsageCharts } from '@/components/Charts';
import { Badge, Button, Card, Dialog, ErrorNote, Loading, Stat, Tabs } from '@/components/ui';
import { api } from '@/lib/api';
import { fmtInt, fmtMs, fmtUsd } from '@/lib/format';
import { canEdit, rk, useMe } from '@/lib/hooks';
import type { AgentDetail } from '@/lib/types';
import { AgentEditor } from './AgentEditor';
import { AgentPlayground } from './AgentPlayground';
import { AgentCompare } from './AgentCompare';
import { AgentTests } from './AgentTests';
import { AgentVersions } from './AgentVersions';
import { FlowBuilder } from './FlowBuilder';

type Tab = 'overview' | 'editor' | 'versions' | 'playground' | 'tests' | 'compare' | 'requests';

type Series = { bucket: string; requests: number; errors: number; costUsd: number | null; inputTokens: number; outputTokens: number; p50Ms: number | null; p95Ms: number | null }[];

export const AgentPage = ({ agentId, replayTraceId }: { agentId: string; replayTraceId?: string }) => {
  const me = useMe();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: rk('agent', agentId), queryFn: () => api<AgentDetail>(`/v1/agents/${agentId}`) });
  const [tab, setTab] = useState<Tab>(replayTraceId ? 'playground' : 'editor');
  const [showCode, setShowCode] = useState(false);

  const archive = useMutation({
    mutationFn: (archived: boolean) => api(`/v1/agents/${agentId}`, { method: 'PATCH', body: { archived } }),
    onSuccess: (_d, archived) => {
      toast.success(archived ? 'Archived — removed from the runtime' : 'Restored');
      void qc.invalidateQueries({ queryKey: rk('agent', agentId) });
      void qc.invalidateQueries({ queryKey: rk('agents') });
    },
  });

  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorNote error={q.error} />;
  const detail = q.data!;
  const a = detail.agent;
  const live = detail.versions.find((v) => v.id === a.publishedVersionId);

  return (
    <>
      <Link to="/" className="mb-2 inline-flex items-center gap-1 text-xs text-ink-2 hover:text-ink">
        <ChevronLeft className="size-3.5" /> Agents
      </Link>
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-2">
            {a.name}
            {a.kind === 'flow' && <Badge tone="brand">flow</Badge>}
            {live ? <Badge tone="ok">live v{live.version}</Badge> : <Badge>not published</Badge>}
            {a.canaryPercent > 0 && <Badge tone="brand">canary {a.canaryPercent}%</Badge>}
            {a.archivedAt && <Badge tone="bad">archived</Badge>}
          </span>
        }
        subtitle={
          <span>
            <span className="mono">{a.slug}</span> · {a.project}
            {a.description ? ` · ${a.description}` : ''}
          </span>
        }
        actions={
          <>
            {live && <PortabilityActions agentId={a.id} slug={a.slug} />}
            <Button variant="ghost" onClick={() => setShowCode(true)}>
              <Code2 className="size-4" /> Call from code
            </Button>
            {canEdit(me.data) && (
              <Button variant="ghost" loading={archive.isPending} onClick={() => (a.archivedAt || confirm('Archive? Hosts stop finding this agent within 30 seconds.')) && archive.mutate(!a.archivedAt)}>
                {a.archivedAt ? <ArchiveRestore className="size-4" /> : <Archive className="size-4" />}
                {a.archivedAt ? 'Restore' : 'Archive'}
              </Button>
            )}
          </>
        }
      />
      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          { value: 'editor', label: a.kind === 'flow' ? 'Builder' : 'Editor' },
          { value: 'overview', label: 'Overview' },
          { value: 'versions', label: `Versions (${detail.versions.length})` },
          { value: 'playground', label: 'Playground' },
          { value: 'tests', label: 'Tests' },
          ...(a.kind === 'flow' ? [] : [{ value: 'compare' as const, label: 'Compare models' }]),
          { value: 'requests', label: 'Requests' },
        ]}
      />
      <div className="mt-5">
        {tab === 'editor' && (a.kind === 'flow' ? <FlowBuilder key={a.id} detail={detail} /> : <AgentEditor key={a.id} detail={detail} />)}
        {tab === 'overview' && (
          <div className="flex flex-col gap-5">
            <Card title="Connections">
              <AgentMap focusId={a.id} height={340} />
            </Card>
            <Overview agentId={a.id} />
          </div>
        )}
        {tab === 'versions' && <AgentVersions detail={detail} />}
        {tab === 'playground' && <AgentPlayground key={replayTraceId} detail={detail} replayTraceId={replayTraceId} />}
        {tab === 'tests' && <AgentTests detail={detail} />}
        {tab === 'compare' && <AgentCompare detail={detail} />}
        {tab === 'requests' && <TraceList fixed={{ agentId: a.id }} showAgent={false} />}
      </div>
      <CodeDialog open={showCode} onClose={() => setShowCode(false)} detail={detail} />
    </>
  );
};

const Overview = ({ agentId }: { agentId: string }) => {
  const [hours, setHours] = useState(24);
  const q = useQuery({ queryKey: rk('stats', agentId, hours), queryFn: () => api<{ bucket: 'hour' | 'day'; series: Series }>(`/v1/stats?agentId=${agentId}&hours=${hours}`) });
  const s = q.data?.series ?? [];
  const sum = (k: 'requests' | 'errors' | 'inputTokens' | 'outputTokens') => s.reduce((a, r) => a + r[k], 0);
  const costs = s.map((r) => r.costUsd).filter((c): c is number => c !== null);
  const p95 = s.length ? Math.max(...s.map((r) => r.p95Ms ?? 0)) : null;
  return (
    <div className="flex flex-col gap-5">
      <Card
        title="Production traffic"
        actions={
          <div className="flex gap-1">
            {[24, 24 * 7, 24 * 30].map((h) => (
              <Button key={h} size="sm" variant={h === hours ? 'primary' : 'ghost'} onClick={() => setHours(h)}>
                {h === 24 ? '24h' : `${h / 24}d`}
              </Button>
            ))}
          </div>
        }
      >
        {q.isLoading ? (
          <Loading />
        ) : (
          <div className="grid grid-cols-2 gap-5 p-4 sm:grid-cols-3 lg:grid-cols-6">
            <Stat label="Requests" value={fmtInt(sum('requests'))} />
            <Stat label="Errors" value={fmtInt(sum('errors'))} tone={sum('errors') ? 'bad' : undefined} />
            <Stat label="Error rate" value={sum('requests') ? `${((sum('errors') / sum('requests')) * 100).toFixed(1)}%` : '—'} />
            <Stat label="Worst p95" value={fmtMs(p95)} />
            <Stat label="Tokens in / out" value={`${fmtInt(sum('inputTokens'))} / ${fmtInt(sum('outputTokens'))}`} />
            <Stat label="Cost" value={costs.length ? fmtUsd(costs.reduce((a, b) => a + b, 0)) : '—'} />
          </div>
        )}
      </Card>
      {q.data && <UsageCharts rows={q.data.series} hours={hours} bucket={q.data.bucket} />}
      <TraceList fixed={{ agentId }} showAgent={false} />
    </div>
  );
};

const CodeDialog = ({ open, onClose, detail }: { open: boolean; onClose: () => void; detail: AgentDetail }) => {
  const live = detail.versions.find((v) => v.id === detail.agent.publishedVersionId) ?? detail.versions[0];
  const vars = live?.config.variables ?? [];
  const cfg = live?.config;
  const json = Boolean(cfg && (!('output' in cfg) || (cfg.output?.type && cfg.output.type !== 'text')));
  const snippet = `import { agents } from '../lib/agents';

const { output, traceId } = await agents.run${json ? '<MyOutput>' : ''}('${detail.agent.slug}', {
  variables: { ${vars.map((v) => `${v.name}: ${v.name}`).join(', ')} },${!cfg || !('system' in cfg) || cfg.userTemplate ? '' : "\n  input: 'user message',"}
  subject: { userId, applicationId },   // filterable in Requests
});`;
  return (
    <Dialog open={open} onClose={onClose} title="Call this agent from code" wide footer={<Button onClick={onClose}>Close</Button>}>
      <pre className="overflow-auto rounded-md border border-line bg-line-2/60 p-3">{snippet}</pre>
      <p className="mt-3 text-[13px] text-ink-2">
        Streaming: <span className="mono">for await (const ev of agents.stream(slug, opts))</span> yields <span className="mono">delta</span>, <span className="mono">tool_call</span>,{' '}
        <span className="mono">handoff</span> and a final <span className="mono">done</span>. The call keeps working from the last-known config if the Agent Manager is unreachable.
      </p>
    </Dialog>
  );
};
